import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { ContentProvider } from "@/context/ContentContext";
import { CategoriesProvider } from "@/context/CategoriesContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import AnnouncementBar from "@/components/AnnouncementBar";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Track from "@/pages/Track";
import Wishlist from "@/pages/Wishlist";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Legal from "@/pages/Legal";
import ComingSoon from "@/pages/ComingSoon";
import Admin from "@/pages/Admin";
import { useContent } from "@/context/ContentContext";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function usePreviewMode() {
  const params = new URLSearchParams(window.location.search);
  if (params.get("preview") === "1") {
    sessionStorage.setItem("lnr_preview", "1");
  } else if (params.get("preview") === "0") {
    sessionStorage.removeItem("lnr_preview");
  }
  return sessionStorage.getItem("lnr_preview") === "1";
}

function PreviewBar() {
  const exit = () => {
    sessionStorage.removeItem("lnr_preview");
    window.location.href = "/";
  };
  return (
    <div
      data-testid="preview-bar"
      className="flex items-center justify-center gap-3 bg-[#0a3d91] px-4 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-white"
    >
      <span>Mod previzualizare · site-ul e în „Coming Soon" pentru vizitatori</span>
      <button data-testid="preview-exit" onClick={exit} className="underline hover:opacity-80">
        Ieși
      </button>
    </div>
  );
}

function Storefront({ children }) {
  const content = useContent();
  const isPreview = usePreviewMode();
  const comingSoon = !!content?.coming_soon?.enabled;

  if (comingSoon && !isPreview) {
    return <ComingSoon content={content} />;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SmoothScroll />
      {isPreview && comingSoon ? <PreviewBar /> : null}
      <AnnouncementBar />
      <Header />
      <CartDrawer />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <LanguageProvider>
          <CartProvider>
            <WishlistProvider>
              <ContentProvider>
                <CategoriesProvider>
                <BrowserRouter>
                  <ScrollTop />
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                    <Route path="/" element={<Storefront><Home /></Storefront>} />
                    <Route path="/shop" element={<Storefront><Shop /></Storefront>} />
                    <Route path="/product/:slug" element={<Storefront><Product /></Storefront>} />
                    <Route path="/checkout" element={<Storefront><Checkout /></Storefront>} />
                    <Route path="/order/:id" element={<Storefront><OrderConfirmation /></Storefront>} />
                    <Route path="/payment/success" element={<Storefront><PaymentSuccess /></Storefront>} />
                    <Route path="/payment/cancel" element={<Storefront><PaymentCancel /></Storefront>} />
                    <Route path="/account" element={<Storefront><Account /></Storefront>} />
                    <Route path="/track" element={<Storefront><Track /></Storefront>} />
                    <Route path="/wishlist" element={<Storefront><Wishlist /></Storefront>} />
                    <Route path="/about" element={<Storefront><About /></Storefront>} />
                    <Route path="/faq" element={<Storefront><FAQ /></Storefront>} />
                    <Route path="/legal/:doc" element={<Storefront><Legal /></Storefront>} />
                  </Routes>
                  <Toaster position="bottom-right" />
                </BrowserRouter>
                </CategoriesProvider>
              </ContentProvider>
            </WishlistProvider>
          </CartProvider>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
